import React from "react";
import ReactModal from "react-modal";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";

// Ensure ReactModal knows its parent element
if (typeof window !== "undefined" && document.getElementById("root")) {
  ReactModal.setAppElement("#root");
}

const GameInstructionModal = ({
  isOpen,
  onRequestClose,
  title = "Game Instructions",
  instructions = [],
  gamePath = "",
  icon = "solar:gamepad-bold",
  isBinocular = false,
}) => {
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={title}
      overlayClassName="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 transition-opacity duration-300"
      className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden transform transition-all duration-300 scale-100 outline-none"
    >
      {/* Header Visual Accent */}
      <div className={`h-2.5 w-full bg-gradient-to-r ${
        isBinocular ? "from-red-500 via-purple-500 to-blue-500" : "from-[#291D89] to-[#4F46E5]"
      }`} />

      {/* Close Button */}
      <button
        onClick={onRequestClose}
        className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200 focus:outline-none"
        aria-label="Close modal"
      >
        <Icon icon="solar:close-circle-bold" className="text-2xl" />
      </button>

      <div className="p-8">
        {/* Game Icon & Title */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-md mb-4 ${
            isBinocular 
              ? "bg-gradient-to-tr from-red-500/10 to-blue-500/10 text-purple-600" 
              : "bg-indigo-50 text-[#291D89]"
          }`}>
            <Icon icon={icon} className="text-4xl" />
          </div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">
            {title}
          </h2>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">
            {isBinocular ? "Red-Blue Binocular Therapy" : "Monocular Vision Therapy"}
          </p>
        </div>

        {/* Instructions List */}
        <div className="bg-slate-50/80 rounded-2xl p-5 border border-slate-100 mb-8">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Icon icon="solar:clipboard-list-bold" className="text-base text-slate-500" />
            How to Play
          </h3>
          <ul className="space-y-3">
            {instructions.map((inst, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-slate-600 font-semibold leading-relaxed">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mt-0.5">
                  <Icon icon="solar:check-circle-bold" className="text-base" />
                </span>
                <span>{inst.replace(/^\s*•\s*/, "")}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={onRequestClose}
            className="flex-1 py-3.5 px-5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 font-bold text-sm transition duration-200"
          >
            Cancel
          </button>
          
          <Link to={gamePath} className="flex-1">
            <button className={`w-full py-3.5 px-5 rounded-xl text-white font-bold text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition duration-200 flex items-center justify-center gap-2 ${
              isBinocular 
                ? "bg-gradient-to-r from-red-500 via-purple-600 to-blue-500 hover:from-red-600 hover:to-blue-600" 
                : "bg-[#291D89] hover:bg-[#291D89]/95"
            }`}>
              <Icon icon="solar:play-bold" className="text-base" />
              Start Therapy
            </button>
          </Link>
        </div>
      </div>
    </ReactModal>
  );
};

export default GameInstructionModal;
